import GraphXAxisLabelItem from 'components/atoms/GraphXaxisLabelItem';
import Barchart from 'components/molecules/Barchart';
import Legend from 'components/molecules/Legend';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { barWidth, salesGraphWidth } from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import { yAxisGraphWidth } from 'components/template/CommercialForecast/CFExpandedView';
import { height } from 'components/template/GrowthAnalysis/ExpandedView';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { IChartConfig } from 'parser/pricingAnalytics/tierAnalyticsParser';
import React, { useMemo, useRef } from 'react';
import { formatNumWithKMBAlt, scaleMaxValue, scaleMinValue } from 'utils/helperMethods';
export const containerId = 'tier-analytics-chart-container';
interface ILabelObject {
    mainLabel: string;
    xAxis: string;
    yAxis: string;
}
interface IProps {
    labelObject: ILabelObject;
    chartConfig: IChartConfig;
    decimalForNonPercentage: number;
    decimalForPercentage: number;
    chartContainer: string;
}
const TierAnalyticsChart: React.FC<IProps> = ({
    labelObject,
    chartConfig,
    decimalForPercentage,
    chartContainer,
    decimalForNonPercentage,
}) => {
    const chartRef = useRef(null);
    const containerWidth = useContainerWidth({
        containerId: containerId,
        buffer: yAxisGraphWidth + 34 * 2 + 112,
    });
    const graphViewContainerWidth = Math.max(
        chartConfig?.chartData ? chartConfig.chartData?.length * salesGraphWidth : 0,
        containerWidth / 2,
    );
    const perGraphComponentWidth = graphViewContainerWidth / chartConfig?.chartData?.length || 0;
    const excelData = useMemo(
        () => chartConfig?.chartData.map((item) => [item.category, item.sales]),
        [chartConfig?.chartData],
    );

    const getFormattedVal = (val: number, suffix?: string) => {
        if (suffix === '%') return `${formatNumWithKMBAlt(val, decimalForPercentage, decimalForPercentage)}%`;
        return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
    };
    return (
        <div ref={chartRef}>
            <div className="flex items-center gap-1 justify-between">
                <p className="text-subHeaderColor font-sm font-medium">{labelObject?.mainLabel}</p>
                <span className="flex items-center gap-2">
                    <CopyToClipBoard refOfComponent={chartRef} chartOnly={true} />
                    <DownloadExcel
                        title={labelObject?.mainLabel}
                        excelData={excelData}
                        headers={[labelObject?.xAxis, labelObject?.yAxis]}
                        excludeFilters
                    />
                </span>
            </div>
            {/* LEGEND WRAPPER */}
            <div className="flex justify-between my-4">
                <p className="font-xs w-20 text-lightBlack">{labelObject?.yAxis}</p>
                <Legend data={chartConfig?.legendData} />
                <p className="w-3"></p>
            </div>
            <div className="flex ml-1 w-full">
                {/* Y AXIS */}
                <div style={{ minWidth: yAxisGraphWidth }}>
                    <Barchart
                        colorKey="color"
                        dataset={[]}
                        height={height}
                        loading={false}
                        chartContainer={chartContainer}
                        fields={['category', 'sales']}
                        width={yAxisGraphWidth}
                        YGrid={false}
                        padding={[0, 0, 0, 0]}
                        yAxisLabelFormatter={(val: any) => {
                            return getFormattedVal(val, chartConfig?.suffix);
                        }}
                        xAxisConfig={false}
                        minBound={scaleMinValue(80, chartConfig?.minVal)}
                        maxBound={scaleMaxValue(80, chartConfig?.maxVal)}
                    />
                </div>
                {/* MAIN AXIS */}
                <div className="flex pb-[45px]" style={{ maxWidth: '100%', overflowX: 'auto' }}>
                    {chartConfig?.chartData.map((dataItem: any, index: number) => {
                        return (
                            <div>
                                <div style={{ height: height }}>
                                    <Barchart
                                        barWidth={barWidth}
                                        //yAxisConfig={false}
                                        dataset={[dataItem]}
                                        height={height}
                                        loading={false}
                                        chartContainer={`${chartContainer}-${index}`}
                                        fields={['category', 'sales']}
                                        minBound={scaleMinValue(80, chartConfig?.minVal)}
                                        maxBound={scaleMaxValue(80, chartConfig?.maxVal)}
                                        width={perGraphComponentWidth || salesGraphWidth}
                                        tooltipFormatter={(xAxis, val) => {
                                            return {
                                                name: xAxis,
                                                value: getFormattedVal(val, chartConfig?.suffix),
                                            };
                                        }}
                                        intervalFormatter={(val) => {
                                            return getFormattedVal(val, chartConfig?.suffix);
                                        }}
                                        colorKey={'color'}
                                    />
                                </div>
                                <div className="flex items-center">
                                    <GraphXAxisLabelItem
                                        width={perGraphComponentWidth || salesGraphWidth}
                                        item={dataItem}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <p className="text-lightBlack font-xs relative top-[-40px] left-[25px] flex justify-center">
                {labelObject?.xAxis}
            </p>
        </div>
    );
};

export default TierAnalyticsChart;
